import { type CandidateMetrics, type CandidateStatEntry } from "@crowdcoursing/api/openapi";
import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Typography,
} from "@mui/material";
import Highcharts from "highcharts";
import Funnel from "highcharts/modules/funnel";
import HighchartsReact from "highcharts-react-official";
import { DateTime } from "luxon";
import { useEffect, useMemo, useRef, useState } from "react";

import { PopoverDateRangePicker } from "~ui/atoms/Form/DateRange/PopoverDateRangePicker";
Funnel(Highcharts);

type ChartType = "pie" | "column" | "bar" | "line" | "area";
type ChartView = "pipeline" | "funnel" | "percentage" | "time-series";

interface TimeRange {
  startDate: DateTime;
  endDate: DateTime;
}

type TimeSeriesMetric =
  | "totalStudents"
  | "studentsWithResume"
  | "studentsWithInterest"
  | "studentsWithInterviewRequests"
  | "studentsWithInterviewsAttended"
  | "studentsPlaced"
  | "all";

const defaultTitle = "Candidacy Metrics";
const defaultTimeRange = {
  endDate: DateTime.now(),
  startDate: DateTime.now().minus({ months: 1 }),
};

export interface StudentCandidacyMetricsChartProps {
  title?: string;
  timeSeriesData: CandidateStatEntry[] | null;
  onTimeRangeChange?: (timeRange: TimeRange) => void;
  loading?: boolean;
  error?: string;
  sampleSize?: number;
}

interface ChartContentProps {
  title: string;
  timeSeriesData: CandidateStatEntry[];
  onTimeRangeChange?: (timeRange: TimeRange) => void;
  loading: boolean;
  chartType: ChartType;
  setChartType: (type: ChartType) => void;
  chartView: ChartView;
  setChartView: (view: ChartView) => void;
  timeSeriesMetric: TimeSeriesMetric;
  setTimeSeriesMetric: (metric: TimeSeriesMetric) => void;
  timeRange: TimeRange;
  setTimeRange: (range: TimeRange) => void;
  sampleSize?: number;
  setCurrentSampleSize: (size: number) => void;
}

const ChartContent = ({
  title,
  timeSeriesData,
  onTimeRangeChange,
  loading,
  chartType,
  setChartType,
  chartView,
  setChartView,
  timeSeriesMetric,
  setTimeSeriesMetric,
  timeRange,
  setTimeRange,
  sampleSize = 20,
  setCurrentSampleSize,
}: ChartContentProps) => {
  const hasEnoughDataPoints = timeSeriesData.length >= 2;
  const chartInstanceRef = useRef<Highcharts.Chart | null>(null);

  useEffect(() => {
    if (loading && chartInstanceRef.current) {
      chartInstanceRef.current.showLoading();
    } else if (chartInstanceRef.current) {
      chartInstanceRef.current.hideLoading();
    }
  }, [loading]);

  useEffect(() => {
    if (chartView === "time-series" && !hasEnoughDataPoints) {
      setChartView("pipeline");
    }
  }, [chartView, hasEnoughDataPoints, setChartView]);

  useEffect(() => {
    if (chartView === "time-series" && (chartType === "column" || chartType === "bar" || chartType === "pie")) {
      setChartType("line");
    }
  }, [chartView, chartType, setChartType]);

  useEffect(() => {
    if (chartView !== "time-series" && (chartType === "line" || chartType === "area")) {
      setChartType("column");
    }
  }, [chartView, chartType, setChartType]);

  const getMostRecentTimeSeriesData = (): CandidateMetrics => {
    if (!timeSeriesData || timeSeriesData.length === 0) {
      return {
        studentsPlaced: 0,
        studentsWithInterest: 0,
        studentsWithInterviewRequests: 0,
        studentsWithInterviewsAttended: 0,
        studentsWithResume: 0,
        totalStudents: 0,
      };
    }

    const sortedData = [...timeSeriesData].sort((a, b) => {
      const dateA = a?.timestamp ? new Date(a.timestamp).getTime() : 0;
      const dateB = b?.timestamp ? new Date(b.timestamp).getTime() : 0;
      return dateB - dateA;
    });

    return (
      sortedData[0]?.metrics || {
        studentsPlaced: 0,
        studentsWithInterest: 0,
        studentsWithInterviewRequests: 0,
        studentsWithInterviewsAttended: 0,
        studentsWithResume: 0,
        totalStudents: 0,
      }
    );
  };

  const data = useMemo(() => getMostRecentTimeSeriesData(), [timeSeriesData]);

  const categories = [
    "Total Candidates",
    "With Résumé",
    "Company Interest",
    "Interview Requests",
    "Interviews Attended",
    "Placed",
  ];

  const values = [
    data?.totalStudents || 0,
    data?.studentsWithResume || 0,
    data?.studentsWithInterest || 0,
    data?.studentsWithInterviewRequests || 0,
    data?.studentsWithInterviewsAttended || 0,
    data?.studentsPlaced || 0,
  ];

  const percentages = values.map((value) => {
    const totalStudents = data?.totalStudents || 1;
    return (((value || 0) / totalStudents) * 100).toFixed(1);
  });

  const handleChartTypeChange = (event: SelectChangeEvent) => {
    const newType = event.target.value as ChartType;
    setChartType(newType);

    if (chartView === "time-series" && newType === "pie") {
      setChartType("line");
    }
  };

  const handleChartViewChange = (event: SelectChangeEvent) => {
    const newView = event.target.value as ChartView;
    setChartView(newView);

    if (newView === "time-series" && chartType === "pie") {
      setChartType("line");
    }
  };

  const handleTimeSeriesMetricChange = (event: SelectChangeEvent) => {
    setTimeSeriesMetric(event.target.value as TimeSeriesMetric);
  };

  const metricKeyToName: Record<TimeSeriesMetric, string> = {
    all: "All Metrics",
    studentsPlaced: "Placed",
    studentsWithInterest: "Company Interest",
    studentsWithInterviewRequests: "Interview Requests",
    studentsWithInterviewsAttended: "Interviews Attended",
    studentsWithResume: "With Résumé",
    totalStudents: "Total Candidates",
  };

  const prepareTimeSeriesData = () => {
    const defaultReturn = {
      series: [],
      timestamps: [],
    };

    if (!timeSeriesData || !Array.isArray(timeSeriesData) || timeSeriesData.length < 2) {
      return defaultReturn;
    }

    try {
      const sortedData = [...timeSeriesData].sort((a, b) => {
        const dateA = a?.timestamp ? new Date(a.timestamp).getTime() : 0;
        const dateB = b?.timestamp ? new Date(b.timestamp).getTime() : 0;
        return dateA - dateB;
      });

      const sampleData = (data: CandidateStatEntry[]): CandidateStatEntry[] => {
        const actualSampleSize = sampleSize || 20;

        if (data.length <= actualSampleSize) {
          return data;
        }

        const sampledData: CandidateStatEntry[] = [];

        sampledData.push(data[0]);

        if (data.length > 2) {
          const step = (data.length - 2) / (actualSampleSize - 2);

          for (let i = 1; i <= actualSampleSize - 2; i++) {
            const index = Math.min(Math.floor(step * i), data.length - 2);
            sampledData.push(data[index]);
          }
        }

        sampledData.push(data[data.length - 1]);

        return sampledData;
      };

      const sampledData = sampleData(sortedData);

      const formatDate = (timestamp: string | undefined): string => {
        if (!timestamp) {
          return "Unknown";
        }
        return DateTime.fromISO(timestamp).toFormat("MMM d, yyyy");
      };

      const timestamps = sampledData.map((point) => formatDate(point?.timestamp));

      if (timeSeriesMetric === "all") {
        return {
          series: [
            {
              data: sampledData.map((point) => point?.metrics?.totalStudents || 0),
              name: "Total Candidates",
            },
            {
              data: sampledData.map((point) => point?.metrics?.studentsWithResume || 0),
              name: "With Résumé",
            },
            {
              data: sampledData.map((point) => point?.metrics?.studentsWithInterest || 0),
              name: "Company Interest",
            },
            {
              data: sampledData.map((point) => point?.metrics?.studentsWithInterviewRequests || 0),
              name: "Interview Requests",
            },
            {
              data: sampledData.map((point) => point?.metrics?.studentsWithInterviewsAttended || 0),
              name: "Interviews Attended",
            },
            {
              data: sampledData.map((point) => point?.metrics?.studentsPlaced || 0),
              name: "Placed",
            },
          ],
          timestamps,
        };
      } else {
        return {
          series: [
            {
              data: sampledData.map((point) => point?.metrics?.[timeSeriesMetric] || 0),
              name: metricKeyToName[timeSeriesMetric],
            },
          ],
          timestamps,
        };
      }
    } catch (error) {
      console.error("Error preparing time series data:", error);
      return defaultReturn;
    }
  };

  const timeSeriesChartData = useMemo(() => prepareTimeSeriesData(), [timeSeriesData, timeSeriesMetric, sampleSize]);

  const getChartOptions = () => {
    let options: Highcharts.Options = {};

    const commonChartOptions = {
      animation: false,
      height: null,
      reflow: true,
      width: null,
    };

    if (chartView === "pipeline") {
      options = {
        chart: {
          type: chartType,
          ...commonChartOptions,
        },
        plotOptions: {
          series: {
            dataLabels: {
              enabled: true,
              format: "{point.y}",
            },
          },
        },
        series: [
          {
            colorByPoint: true,
            data: values,
            name: "Candidates",
            type: chartType,
          } as Highcharts.SeriesOptionsType,
        ],
        title: {
          text: `${title} (${chartType === "pie" ? "Pie" : chartType === "column" ? "Column" : "Bar"} Chart)`,
        },
        xAxis: {
          categories,
          title: {
            text: "Pipeline Stage",
          },
        },
        yAxis: {
          title: {
            text: "Number of Candidates",
          },
        },
      };
    } else if (chartView === "funnel") {
      options = {
        chart: {
          type: "funnel",
          ...commonChartOptions,
        },
        plotOptions: {
          funnel: {
            dataLabels: {
              enabled: true,
              format: "<b>{point.name}</b>: {point.y}",
              softConnector: true,
            },
            neckHeight: "25%",
            neckWidth: "30%",
          },
        },
        series: [
          {
            data: categories.map((category, index) => ({
              name: category,
              y: values[index],
            })),
            name: "Candidates",
            type: "funnel",
          } as Highcharts.SeriesOptionsType,
        ],
        title: {
          text: `${title} (Funnel)`,
        },
      };
    } else if (chartView === "percentage") {
      options = {
        chart: {
          type: chartType === "pie" ? "pie" : "column",
          ...commonChartOptions,
        },
        plotOptions: {
          series: {
            dataLabels: {
              enabled: true,
              format: "{point.y}%",
            },
          },
        },
        series: [
          {
            colorByPoint: true,
            data:
              chartType === "pie"
                ? categories.slice(1).map((category, index) => ({
                    name: category,
                    y: parseFloat(percentages[index + 1]),
                  }))
                : percentages.slice(1).map((p) => parseFloat(p)),
            name: "Percentage",
            type: chartType === "pie" ? "pie" : chartType,
          } as Highcharts.SeriesOptionsType,
        ],
        title: {
          text: `${title} (Percentage of Total Candidates)`,
        },
        xAxis: {
          categories: categories.slice(1),
        },
        yAxis: {
          title: {
            text: "Percentage (%)",
          },
        },
      };
    } else if (chartView === "time-series") {
      const { timestamps, series } = timeSeriesChartData;

      options = {
        chart: {
          type: chartType === "pie" ? "line" : chartType,
          ...commonChartOptions,
        },
        legend: {
          enabled: true,
        },
        plotOptions: {
          area: {
            lineColor: "#666666",
            lineWidth: 1,
            marker: {
              lineColor: "#666666",
              lineWidth: 1,
            },
            stacking: "normal",
          },
          series: {
            cursor: "pointer",
            marker: {
              enabled: true,
            },
            pointPlacement: "on",
          },
        },
        series: series.map((s) => ({
          data: s.data,
          name: s.name,
          type: chartType === "pie" ? "line" : chartType,
        })) as Highcharts.SeriesOptionsType[],
        title: {
          text: `${title} Over Time (${timeRange.startDate.toFormat("MMM d, yyyy")} - ${timeRange.endDate.toFormat(
            "MMM d, yyyy",
          )})`,
        },
        tooltip: {
          shared: true,
        },
        xAxis: {
          categories: timestamps,
          title: {
            text: "Date",
          },
        },
        yAxis: {
          title: {
            text: "Number of Candidates",
          },
        },
      };
    }

    return options;
  };

  const chartOptions = useMemo(
    () => getChartOptions(),
    [chartType, chartView, values, percentages, timeRange, timeSeriesChartData, title],
  );

  const handleDateRangeChange = (newDateRange: { startDate: DateTime | null; endDate: DateTime | null }) => {
    if (newDateRange.startDate && newDateRange.endDate) {
      const newTimeRange = {
        endDate: newDateRange.endDate,
        startDate: newDateRange.startDate,
      };
      setTimeRange(newTimeRange);

      if (typeof onTimeRangeChange === "function") {
        onTimeRangeChange(newTimeRange);
      }
    }
  };

  const handleSampleSizeChange = (event: SelectChangeEvent) => {
    const newSize = parseInt(event.target.value, 10);
    setCurrentSampleSize(newSize);
  };

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          <FormControl fullWidth size="small">
            <InputLabel id="chart-view-label">View</InputLabel>
            <Select
              id="chart-view-select"
              label="View"
              labelId="chart-view-label"
              onChange={handleChartViewChange}
              value={chartView}
            >
              <MenuItem value="pipeline">Pipeline</MenuItem>
              <MenuItem value="funnel">Funnel</MenuItem>
              <MenuItem value="percentage">Percentage</MenuItem>
              <MenuItem disabled={!hasEnoughDataPoints} value="time-series">
                Time Series
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          <FormControl fullWidth size="small">
            <InputLabel id="chart-type-label">Chart Type</InputLabel>
            <Select
              disabled={chartView === "funnel"}
              id="chart-type-select"
              label="Chart Type"
              labelId="chart-type-label"
              onChange={handleChartTypeChange}
              value={chartType}
            >
              <MenuItem disabled={chartView === "time-series"} value="column">
                Column
              </MenuItem>
              <MenuItem disabled={chartView === "time-series"} value="bar">
                Bar
              </MenuItem>
              <MenuItem disabled={chartView === "time-series"} value="pie">
                Pie
              </MenuItem>
              <MenuItem disabled={chartView !== "time-series"} value="line">
                Line
              </MenuItem>
              <MenuItem disabled={chartView !== "time-series"} value="area">
                Area
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          <PopoverDateRangePicker label="Time Range" onChange={handleDateRangeChange} value={timeRange} />
        </Grid>
        {chartView === "time-series" && (
          <>
            <Grid
              size={{
                md: 4,
                xs: 12,
              }}
            >
              <FormControl fullWidth size="small">
                <InputLabel id="time-series-metric-label">Metric Focus</InputLabel>
                <Select
                  id="time-series-metric-select"
                  label="Metric Focus"
                  labelId="time-series-metric-label"
                  onChange={handleTimeSeriesMetricChange}
                  value={timeSeriesMetric}
                >
                  <MenuItem value="all">All Metrics</MenuItem>
                  <MenuItem value="totalStudents">Total Candidates</MenuItem>
                  <MenuItem value="studentsWithResume">With Résumé</MenuItem>
                  <MenuItem value="studentsWithInterest">Company Interest</MenuItem>
                  <MenuItem value="studentsWithInterviewRequests">Interview Requests</MenuItem>
                  <MenuItem value="studentsWithInterviewsAttended">Interviews Attended</MenuItem>
                  <MenuItem value="studentsPlaced">Placed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid
              size={{
                md: 4,
                xs: 12,
              }}
            >
              <FormControl fullWidth size="small">
                <InputLabel id="sample-size-label">Sample Size</InputLabel>
                <Select
                  id="sample-size-select"
                  label="Sample Size"
                  labelId="sample-size-label"
                  onChange={handleSampleSizeChange}
                  value={sampleSize.toString()}
                >
                  <MenuItem value="10">10 points</MenuItem>
                  <MenuItem value="20">20 points</MenuItem>
                  <MenuItem value="30">30 points</MenuItem>
                  <MenuItem value="50">50 points</MenuItem>
                  <MenuItem value="100">100 points</MenuItem>
                  <MenuItem value="200">200 points</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </>
        )}
      </Grid>
      {loading ? (
        <Typography
          aria-label="Loading candidacy metrics"
          aria-live="polite"
          color="text.secondary"
          role="status"
          sx={{ mb: 1, textAlign: "center" }}
          variant="body2"
        >
          Loading data...
        </Typography>
      ) : null}
      {timeSeriesData.length === 0 && !loading ? (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary" variant="body1">
            No data for the selected time range. Adjust the time range above to try another period.
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            height: 400,
            position: "relative",
            width: "100%",
          }}
        >
          <HighchartsReact
            callback={(chart: Highcharts.Chart) => {
              chartInstanceRef.current = chart;
            }}
            containerProps={{ style: { height: "100%", width: "100%" } }}
            highcharts={Highcharts}
            options={chartOptions}
          />
        </Box>
      )}
    </>
  );
};

const CHART_VIEW_STORAGE_KEY = "studentCandidacyMetrics.chartView";
const CHART_TYPE_STORAGE_KEY = "studentCandidacyMetrics.chartType";

const getInitialChartView = (): ChartView => {
  const stored = localStorage.getItem(CHART_VIEW_STORAGE_KEY);
  if (stored === "pipeline" || stored === "funnel" || stored === "percentage" || stored === "time-series") {
    return stored;
  }
  return "pipeline";
};

const getInitialChartType = (): ChartType => {
  const stored = localStorage.getItem(CHART_TYPE_STORAGE_KEY);
  if (stored === "pie" || stored === "column" || stored === "bar" || stored === "line" || stored === "area") {
    return stored;
  }
  return "column";
};

export const StudentCandidacyMetricsChart = (props: StudentCandidacyMetricsChartProps) => {
  const { timeSeriesData, error, loading, title = defaultTitle, sampleSize: initialSampleSize = 20 } = props;

  const [chartType, setChartTypeState] = useState<ChartType>(getInitialChartType);
  const [chartView, setChartViewState] = useState<ChartView>(getInitialChartView);
  const [timeSeriesMetric, setTimeSeriesMetric] = useState<TimeSeriesMetric>("all");
  const [timeRange, setTimeRange] = useState<TimeRange>(defaultTimeRange);
  const [currentSampleSize, setCurrentSampleSize] = useState<number>(initialSampleSize);

  const setChartType = (type: ChartType) => {
    setChartTypeState(type);
    localStorage.setItem(CHART_TYPE_STORAGE_KEY, type);
  };

  const setChartView = (view: ChartView) => {
    setChartViewState(view);
    localStorage.setItem(CHART_VIEW_STORAGE_KEY, view);
  };

  const chartData = timeSeriesData && Array.isArray(timeSeriesData) ? timeSeriesData : [];

  return (
    <Box sx={{ width: "100%" }}>
      {error ? (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      ) : null}
      <ChartContent
        chartType={chartType}
        chartView={chartView}
        loading={loading || false}
        onTimeRangeChange={props.onTimeRangeChange}
        sampleSize={currentSampleSize}
        setChartType={setChartType}
        setChartView={setChartView}
        setCurrentSampleSize={setCurrentSampleSize}
        setTimeRange={setTimeRange}
        setTimeSeriesMetric={setTimeSeriesMetric}
        timeRange={timeRange}
        timeSeriesData={chartData}
        timeSeriesMetric={timeSeriesMetric}
        title={title}
      />
    </Box>
  );
};
